import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, onAuthStateChanged, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { auth } from '../../Firebase';
import Swal from 'sweetalert2';
import { HiLockClosed, HiEnvelope, HiArrowRight } from 'react-icons/hi2';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate('/admin/dashboard');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await setPersistence(auth, browserSessionPersistence);
      await signInWithEmailAndPassword(auth, email.trim(), password);
      sessionStorage.setItem('admin_last_activity', Date.now().toString());
      Swal.fire({
        title: 'Authentication Successful',
        text: 'Welcome back to the Green Gusto Admin Management console.',
        icon: 'success',
        confirmButtonColor: '#0F5A3A',
      });
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Firebase login error:', error);
      let errorMsg = 'Invalid administrator email or password.';
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        errorMsg = 'Invalid email address or password. Please verify your credentials in Firebase.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMsg = 'Access temporarily disabled due to many failed attempts. Please try again later.';
      } else if (error.message) {
        errorMsg = error.message;
      }
      Swal.fire({
        title: 'Login Failed',
        text: errorMsg,
        icon: 'error',
        confirmButtonColor: '#E05A17',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f18] text-white flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden font-sans">
      {/* Decorative Blur Orbs */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-accent/10 rounded-full blur-[120px]" />

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10 text-center">
        <img
          src="/Green Gusto.png"
          alt="Green Gusto Logo"
          className="h-16 mx-auto mb-6 brightness-125 contrast-125 object-contain"
        />
        <h2 className="text-3xl font-display font-extrabold tracking-tight text-white mb-2">
          Admin Portal Login
        </h2>
        <p className="text-sm text-white/50">
          Sign in with authorized administrator credentials.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-[#111827]/80 backdrop-blur-md py-8 px-6 shadow-premium rounded-3xl border border-white/5 sm:px-10">
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-white/40">
                  <HiEnvelope className="w-4 h-4" />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter admin email address"
                  className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-sm text-white placeholder-white/30 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-white/40">
                  <HiLockClosed className="w-4 h-4" />
                </div>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-sm text-white placeholder-white/30 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all outline-none"
                />
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-green justify-center py-3.5 rounded-xl text-sm font-bold disabled:opacity-60 transition-all shadow-lg flex items-center gap-2"
              >
                {isLoading ? 'Verifying...' : 'Sign In'}
                <HiArrowRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
